import { Badge, Box, Card, Group, Progress, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconCircleCheck, IconCircleDashed, IconClockHour4 } from '@tabler/icons-react';
import type { CSSProperties } from 'react';
import { type ClientDocumentDto, type ClientDocumentStatus, type ClientRequestDetailDto, type ClientStage } from '@mexp/shared';
import { FileList } from '@/features/files/FileList';
import { dayMonth, fullDate, stageLabel, STAGE_COLOR, STAGE_ORDER } from './stage';

const hue = (stage: ClientStage) => STAGE_COLOR[stage];
const stageVars = (stage: ClientStage) =>
  ({
    '--status-fill': `var(--mantine-color-${hue(stage)}-filled)`,
    '--status-text': `var(--mantine-color-${hue(stage)}-light-color)`,
  }) as CSSProperties;

/** The four stages as one track: done and current segments in the stage's hue; In process fills with the documents. */
function StageTrack({ r }: { r: ClientRequestDetailDto }) {
  const current = r.stage === 'completed' ? STAGE_ORDER.length - 1 : STAGE_ORDER.indexOf(r.stage);
  const done = r.stage === 'completed';
  const released = r.releases[0]?.releasedAt ?? r.completedAt;
  return (
    <ol className="portal-status-track" aria-label="Stages">
      {STAGE_ORDER.map((s, i) => {
        const state = done || i < current ? 'done' : i === current ? 'current' : 'next';
        const fill =
          state === 'next' ? 0 : state === 'current' && s === 'in_preparation' && r.progress.total > 0 ? Math.max(6, (r.progress.ready / r.progress.total) * 100) : 100;
        const caption =
          s === 'received'
            ? `${dayMonth(r.createdAt.slice(0, 10))}`
            : s === 'completed' && done && released
              ? dayMonth(released.slice(0, 10))
              : state === 'current'
                ? s === 'in_preparation' && r.progress.total > 0
                  ? `${r.progress.ready} of ${r.progress.total} ready`
                  : 'Now'
                : null;
        const Icon = state === 'done' ? IconCircleCheck : state === 'current' ? IconClockHour4 : IconCircleDashed;
        return (
          <li key={s} className="portal-status-step" data-state={state} aria-current={state === 'current' ? 'step' : undefined}>
            <span className="portal-status-bar" aria-hidden>
              <span style={{ width: `${fill}%` }} />
            </span>
            <Group gap={6} wrap="nowrap" mt={10}>
              <Icon size={16} stroke={1.8} className="portal-status-icon" aria-hidden />
              <Text fz="sm" fw={state === 'current' ? 650 : 500} className="portal-status-label">
                {stageLabel(s)}
              </Text>
            </Group>
            {caption && (
              <Text fz="xs" c="dimmed" className="tabular" pl={22}>
                {caption}
              </Text>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The top of the request page, kept compact: the stage (with any open query or clarification) and the target date on
 * one line, then the stages as a track. A cancellation shows here.
 */
export function RequestStatus({ r }: { r: ClientRequestDetailDto }) {
  const openQueries = r.changeRequests.filter((c) => c.open);

  if (r.stage === 'cancelled') {
    return (
      <Card p={{ base: 'md', sm: 'xl' }} style={stageVars('cancelled')}>
        <Stack gap={8}>
          <Badge color="red" variant="light" size="lg" w="fit-content">
            {stageLabel('cancelled')}
          </Badge>
          <Title order={2} fz={{ base: 20, sm: 22 }} style={{ letterSpacing: '-0.01em' }}>
            {r.cancelledByClient ? 'You withdrew this requisition' : 'Makin cancelled this dossier'}
          </Title>
          {r.cancelReason && (
            <Text fz="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
              {r.cancelReason}
            </Text>
          )}
        </Stack>
      </Card>
    );
  }

  const completed = r.stage === 'completed';

  return (
    <Card p={{ base: 'md', sm: 'lg' }} className="portal-status" style={stageVars(r.stage)}>
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Group gap={8}>
            <Badge color={hue(r.stage)} variant="light" size="lg">
              {stageLabel(r.stage)}
            </Badge>
            {[...openQueries].reverse().map((q) => (
              <Badge key={q.id} color="grape" variant="outline" size="lg">
                Query {q.number} open
              </Badge>
            ))}
            {r.openClarifications > 0 && (
              <Badge color="orange" variant="outline" size="lg">
                Answer needed
              </Badge>
            )}
          </Group>
          {!completed && (
            <Text fz="sm" c="dimmed" className="tabular">
              Target completion date:{' '}
              <Text span fz="sm" fw={650} c="var(--mantine-color-text)">
                {r.requiredBy ? fullDate(r.requiredBy) : 'To be confirmed'}
              </Text>
            </Text>
          )}
        </Group>
        <StageTrack r={r} />
      </Stack>
    </Card>
  );
}

// ---- Documents -------------------------------------------------------------------------------------------------------

const DOC_GROUPS: { status: ClientDocumentStatus; label: string; color: string; icon: typeof IconCircleCheck }[] = [
  { status: 'ready', label: 'Ready', color: 'teal', icon: IconCircleCheck },
  { status: 'in_preparation', label: 'In process', color: 'brand', icon: IconClockHour4 },
  { status: 'pending', label: 'Not started', color: 'gray', icon: IconCircleDashed },
];

/** The documents as a checklist: one bar split by status, then the documents grouped Ready, In process, Not started. */
export function DocumentChecklist({ r }: { r: ClientRequestDetailDto }) {
  const groups = DOC_GROUPS.map((g) => ({ ...g, docs: r.documents.filter((d) => d.status === g.status) }));
  const total = r.documents.length;
  return (
    <Card>
      <Stack gap="lg">
        <Stack gap={10}>
          <Group justify="space-between" align="baseline">
            <Title order={4}>Documents</Title>
            {r.progress.total > 0 && (
              <Text fz="sm" c="dimmed" className="tabular">
                {r.progress.ready} of {r.progress.total} ready
              </Text>
            )}
          </Group>
          {total > 0 && (
            <>
              <Progress.Root size={8} radius="xl" aria-hidden>
                {groups.map((g) =>
                  g.docs.length ? <Progress.Section key={g.status} value={(g.docs.length / total) * 100} color={g.status === 'pending' ? 'gray.3' : g.color} /> : null,
                )}
              </Progress.Root>
              <Group gap="md" className="tabular">
                {groups.map((g) => (
                  <Group key={g.status} gap={6} wrap="nowrap">
                    <Box
                      w={8}
                      h={8}
                      style={{ borderRadius: 999, flex: 'none', background: g.status === 'pending' ? 'var(--mantine-color-gray-4)' : `var(--mantine-color-${g.color}-filled)` }}
                    />
                    <Text fz="xs" c="dimmed">
                      {g.docs.length} {g.label.toLowerCase()}
                    </Text>
                  </Group>
                ))}
              </Group>
            </>
          )}
        </Stack>

        {total ? (
          groups
            .filter((g) => g.docs.length)
            .map((g) => (
              <Stack key={g.status} gap={10}>
                <Group gap={8}>
                  <Text fz="sm" fw={650} c={g.color === 'gray' ? 'dimmed' : `var(--mantine-color-${g.color}-light-color)`}>
                    {g.label}
                  </Text>
                  <Text fz="xs" c="dimmed" className="tabular">
                    {g.docs.length}
                  </Text>
                </Group>
                {g.docs.map((d) => (
                  <DocumentLine key={d.key} d={d} color={g.color} icon={g.icon} />
                ))}
              </Stack>
            ))
        ) : (
          <Text fz="sm" c="dimmed">
            {r.requestType === 'guidelines'
              ? 'Makin shares the guidelines for this destination.'
              : 'Makin prepares the dossier checklist for this registration.'}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function DocumentLine({ d, color, icon: Icon }: { d: ClientDocumentDto; color: string; icon: typeof IconCircleCheck }) {
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <ThemeIcon size={26} radius="xl" variant="light" color={color} style={{ flex: 'none' }}>
        <Icon size={16} />
      </ThemeIcon>
      <Stack gap={0} style={{ minWidth: 0, flex: 1 }} pt={3}>
        <Group gap={6} wrap="wrap">
          <Text fz="sm" fw={600}>
            {d.name}
          </Text>
          {!d.required && (
            <Badge size="xs" variant="outline" color="gray">
              Optional
            </Badge>
          )}
        </Group>
        {d.notes && (
          <Text fz="xs" c="dimmed">
            {d.notes}
          </Text>
        )}
      </Stack>
    </Group>
  );
}
